/**
 * @fileoverview Упаковка uniform-буферов по правилам выравнивания WGSL
 * @module MikhmelViewer/Cinematic10/UniformLayout
 *
 * WGSL для `var<uniform>` использует правила выравнивания, близкие к std140:
 *   f32 / i32 / u32 → size 4,  align 4
 *   vec2<f32>       → size 8,  align 8
 *   vec3<f32>       → size 12, align 16   ← ловушка: size ≠ align
 *   vec4<f32>       → size 16, align 16
 *   mat4x4<f32>     → size 64, align 16
 *   структура целиком выравнивается до 16 байт
 *
 * Ручная упаковка через смещения в Float32Array — источник трудноуловимых
 * багов (сдвиг на одно поле рушит все последующие). Здесь layout описывается
 * декларативно один раз, а смещения вычисляются автоматически.
 */

const TYPE_INFO = Object.freeze({
  f32:   { size: 4,  align: 4,  components: 1, kind: 'f32' },
  i32:   { size: 4,  align: 4,  components: 1, kind: 'i32' },
  u32:   { size: 4,  align: 4,  components: 1, kind: 'u32' },
  vec2f: { size: 8,  align: 8,  components: 2, kind: 'f32' },
  vec3f: { size: 12, align: 16, components: 3, kind: 'f32' },
  vec4f: { size: 16, align: 16, components: 4, kind: 'f32' },
  vec2i: { size: 8,  align: 8,  components: 2, kind: 'i32' },
  vec2u: { size: 8,  align: 8,  components: 2, kind: 'u32' },
  mat4x4f: { size: 64, align: 16, components: 16, kind: 'f32' },
});

const _align = (offset, alignment) => Math.ceil(offset / alignment) * alignment;

/**
 * Описание layout'а uniform-структуры.
 *
 * @example
 *   const L = new UniformLayout([
 *     ['uResolution', 'vec2f'],
 *     ['uIntensity',  'f32'],
 *     ['uSamples',    'i32'],
 *     ['uTint',       'vec3f'],
 *   ]);
 *   L.set({ uResolution: [1920, 1080], uIntensity: 0.5, uSamples: 24, uTint: [1, 1, 1] });
 *   device.queue.writeBuffer(buf, 0, L.buffer);
 */
export class UniformLayout {
  /**
   * @param {Array<[string, string]>} fields пары [имя, тип]
   */
  constructor(fields) {
    if (!Array.isArray(fields) || fields.length === 0) {
      throw new Error('[UniformLayout] fields must be a non-empty array');
    }

    this._fields = new Map();
    let offset = 0;
    let maxAlign = 16;

    for (const entry of fields) {
      if (!Array.isArray(entry) || entry.length < 2) {
        throw new Error('[UniformLayout] each field must be [name, type]');
      }
      const [name, type] = entry;
      const info = TYPE_INFO[type];
      if (!info) throw new Error(`[UniformLayout] unknown type "${type}" for field "${name}"`);
      if (this._fields.has(name)) throw new Error(`[UniformLayout] duplicate field "${name}"`);

      offset = _align(offset, info.align);
      this._fields.set(name, {
        offset,
        byteOffset: offset,
        size: info.size,
        components: info.components,
        kind: info.kind,
        type,
      });
      offset += info.size;
      maxAlign = Math.max(maxAlign, info.align);
    }

    this.byteLength = _align(offset, maxAlign);
    this.buffer = new ArrayBuffer(this.byteLength);
    this._f32 = new Float32Array(this.buffer);
    this._i32 = new Int32Array(this.buffer);
    this._u32 = new Uint32Array(this.buffer);
    this._dirty = true;
  }

  /** Записать одно поле. */
  setField(name, value) {
    const f = this._fields.get(name);
    if (!f) throw new Error(`[UniformLayout] unknown field "${name}"`);

    const view = f.kind === 'f32' ? this._f32 : (f.kind === 'i32' ? this._i32 : this._u32);
    const base = f.byteOffset >> 2;

    if (f.components === 1) {
      const n = typeof value === 'boolean' ? (value ? 1 : 0) : Number(value);
      view[base] = Number.isFinite(n) ? n : 0;
    } else {
      const arr = value;
      for (let i = 0; i < f.components; i++) {
        const n = Number(arr?.[i]);
        view[base + i] = Number.isFinite(n) ? n : 0;
      }
    }
    this._dirty = true;
    return this;
  }

  /** Записать несколько полей разом. Неизвестные ключи игнорируются. */
  set(values) {
    for (const key of Object.keys(values)) {
      if (this._fields.has(key)) this.setField(key, values[key]);
    }
    return this;
  }

  /** Обнулить буфер. */
  clear() {
    this._f32.fill(0);
    this._dirty = true;
    return this;
  }

  has(name) { return this._fields.has(name); }
  getOffset(name) { return this._fields.get(name)?.byteOffset ?? -1; }

  /** Отладочная распечатка смещений — полезно при расхождении с WGSL. */
  describe() {
    const rows = [];
    for (const [name, f] of this._fields.entries()) {
      rows.push(`  ${String(f.byteOffset).padStart(4)}  ${f.type.padEnd(8)} ${name}`);
    }
    return `UniformLayout (${this.byteLength} bytes)\n${rows.join('\n')}`;
  }
}

/**
 * Пул uniform-буферов: один GPUBuffer на layout, переиспользуется между кадрами.
 * Избавляет от аллокации буфера на каждый проход каждого кадра.
 */
export class UniformPool {
  constructor(device) {
    this._device = device;
    this._buffers = new Map();
    this._destroyed = false;
  }

  /**
   * Получить (или создать) GPUBuffer под конкретный layout и записать в него данные.
   * @returns {GPUBuffer}
   */
  upload(key, layout) {
    if (this._destroyed) throw new Error('[UniformPool] destroyed');
    let buf = this._buffers.get(key);
    if (!buf || buf.size < layout.byteLength) {
      if (buf) buf.destroy();
      buf = this._device.createBuffer({
        label: `uniform:${key}`,
        size: Math.max(layout.byteLength, 16),
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });
      this._buffers.set(key, buf);
    }
    this._device.queue.writeBuffer(buf, 0, layout.buffer, 0, layout.byteLength);
    return buf;
  }

  get(key) { return this._buffers.get(key) || null; }

  destroy() {
    for (const b of this._buffers.values()) {
      try { b.destroy(); } catch { /* уже уничтожен */ }
    }
    this._buffers.clear();
    this._destroyed = true;
  }
}

export { TYPE_INFO };
